/**
 * Custom column picker for MUI DataGrid filter panel.
 * Uses columnInputProps.as — see https://github.com/mui/mui-x/issues/18329
 * When upgrading @mui/x-data-grid, verify GridFilterForm / baseSelectOption props.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import {
  Autocomplete,
  AutocompleteInputChangeReason,
  AutocompleteRenderInputParams,
  Box,
  FilterOptionsState,
} from '@mui/material';
import { getAutocompleteRenderInput } from 'components/common/simpleInputFields/autocompleteRenderInput';
import { formatTranslation } from 'intl';
import { CommonTranslationKeys } from 'intl/keys';
import { useIntl } from 'react-intl';
import { Color } from 'shared/utils/colors';
import { debugLog } from 'shared/logger/debugLog';
import { normalizeForSearch } from 'shared/utils/search';
import {
  FILTER_COLUMN_AUTOCOMPLETE_CLASS,
  FILTER_COLUMN_LISTBOX_MAX_HEIGHT,
} from './constants';
import {
  FilterColumnSelectOption,
  parseFilterColumnSelectOptions,
} from './parseFilterColumnSelectOptions';

export interface SearchableFilterColumnSelectProps {
  value?: string;
  onChange?: (event: { target: { value: string } }) => void;
  label?: string;
  disabled?: boolean;
  size?: 'small' | 'medium';
  className?: string;
  children?: React.ReactNode;
}

const FILTER_COLUMN_SELECT_ID = 'filter-column-select';

export function SearchableFilterColumnSelect({
  value = '',
  onChange,
  label,
  disabled = false,
  size = 'small',
  className,
  children,
}: SearchableFilterColumnSelectProps): React.ReactElement {
  const intl = useIntl();
  const [open, setOpen] = useState(false);
  const options = useMemo(
    () => parseFilterColumnSelectOptions(children),
    [children]
  );
  const selectedOption = options.find((option) => option.value === value);
  const selectedLabel = selectedOption?.label ?? '';
  // What the user types; otherwise the input shows the selected column.
  const [query, setQuery] = useState<string>();
  const inputValue = query ?? selectedLabel;

  useEffect(() => {
    if (process.env.NODE_ENV === 'production') {
      return;
    }

    const childCount = React.Children.count(children);
    if (childCount > 0 && options.length === 0) {
      debugLog(
        '[SearchableFilterColumnSelect] Failed to parse column options from children. ' +
          'MUI GridFilterForm may have changed baseSelectOption structure. ' +
          'See https://github.com/mui/mui-x/issues/18329'
      );
    }
  }, [children, options.length]);

  const noOptionsText = formatTranslation(
    intl,
    CommonTranslationKeys.autocompleteNoOptions
  );

  const baseRenderInput = useMemo(
    () =>
      getAutocompleteRenderInput({
        id: FILTER_COLUMN_SELECT_ID,
        required: false,
        error: '',
        disabled: Boolean(disabled),
        label: label ?? '',
        placeholder: '',
        dense: false,
      }),
    [disabled, label]
  );

  const renderInput = useCallback(
    (params: AutocompleteRenderInputParams) =>
      baseRenderInput({
        ...params,
        inputProps: {
          ...params.inputProps,
          style: {
            ...params.inputProps?.style,
            color: Color.black,
          },
          onMouseDown: (event) => {
            params.inputProps?.onMouseDown?.(event);
            // Open the list on mousedown (like a native select) instead of only focusing the input.
            if (!disabled) {
              event.preventDefault();
              setOpen(true);
            }
          },
        },
      }),
    [baseRenderInput, disabled]
  );

  const filterOptions = useCallback(
    (
      availableOptions: FilterColumnSelectOption[],
      { inputValue: query }: FilterOptionsState<FilterColumnSelectOption>
    ) => {
      const normalizedQuery = normalizeForSearch(query);
      if (!normalizedQuery) {
        return availableOptions;
      }

      return availableOptions.filter((option) =>
        normalizeForSearch(option.label).includes(normalizedQuery)
      );
    },
    []
  );

  const handleInputChange = useCallback(
    (
      _: React.SyntheticEvent,
      newInputValue: string,
      reason: AutocompleteInputChangeReason
    ) => {
      // MUI emits "reset" when opening/blurring and briefly restores the old label — skip to avoid flash.
      if (reason === 'reset') {
        return;
      }

      setQuery(newInputValue);
    },
    []
  );

  const handleClose = useCallback(() => {
    setOpen(false);
    setQuery(undefined);
  }, []);

  const handleChange = useCallback(
    (_: React.SyntheticEvent, newValue: FilterColumnSelectOption | null) => {
      setQuery(undefined);
      onChange?.({
        target: { value: newValue?.value ?? '' },
      });
    },
    [onChange]
  );

  return (
    <Box
      className={[FILTER_COLUMN_AUTOCOMPLETE_CLASS, className]
        .filter(Boolean)
        .join(' ')}
      sx={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        height: '40px',
        maxHeight: '40px',
      }}
    >
      <Autocomplete<
        FilterColumnSelectOption,
        false,
        true,
        false
      >
        size={size}
        fullWidth
        disabled={disabled}
        open={open}
        onOpen={() => setOpen(true)}
        onClose={handleClose}
        openOnFocus
        selectOnFocus={false}
        clearOnBlur={false}
        inputValue={inputValue}
        onInputChange={handleInputChange}
        forcePopupIcon
        popupIcon={<ArrowDropDownIcon fontSize='small' />}
        options={options}
        value={selectedOption ?? undefined}
        disableClearable
        getOptionLabel={(option) => option.label}
        isOptionEqualToValue={(option, currentValue) =>
          option.value === currentValue?.value
        }
        filterOptions={filterOptions}
        onChange={handleChange}
        ListboxProps={{
          style: { maxHeight: FILTER_COLUMN_LISTBOX_MAX_HEIGHT },
        }}
        renderInput={renderInput}
        noOptionsText={noOptionsText}
      />
    </Box>
  );
}
